import React, { ReactNode, useCallback } from 'react'
import classNames from 'classnames'
import styled from 'styled-components'
import { Button as RButton } from 'reakit'
import { CircularProgress } from 'react-md'
import {
  compose,
  layout,
  LayoutProps,
  space,
  SpaceProps,
  typography,
  TypographyProps,
  variant as variantHelper,
} from 'styled-system'

type ButtonProps = SpaceProps &
  LayoutProps &
  TypographyProps & {
    id?: string
    type?: 'button' | 'submit' | 'reset'
    value?: string
    className?: string
    children?: ReactNode
    onClick: any
    isEndIcon?: boolean
    disabled?: boolean
    block?: boolean
    expanded?: boolean
    variant?: string
    isLoading?: boolean
    icon?: string | ReactNode
    size?: 'verySmall' | 'small' | 'medium' | 'large' | 'extraLarge'
    background?: string
    mobileFixed?: boolean
  }

const Button: React.FC<ButtonProps> = (props: ButtonProps) => {
  const {
    className,
    block,
    expanded,
    disabled,
    id,
    type,
    value,
    onClick,
    isLoading,
    size,
    isEndIcon = false,
    mobileFixed = false,
    ...restProps
  } = props

  const buttonClasses = classNames(className, {
    block: block,
    expanded: expanded,
  })

  const ACCESSIBILITY_PROPS = {
    'aria-busy': true,
    'aria-describedby': 'fake-feed-loading-progress',
  }

  const getIcon = useCallback(
    (icon: string | ReactNode) => (
      <IconContainer hasChildren={!!props.children} isEndIcon={isEndIcon}>
        {typeof icon === 'string' ? <img src={icon} className={'button__icon'} /> : props.icon}
      </IconContainer>
    ),
    [props],
  )

  return (
    <ButtonContainer
      id={id}
      type={type}
      className={buttonClasses}
      value={value}
      onClick={onClick}
      disabled={disabled}
      $size={size}
      $mobileFixed={mobileFixed}
      {...restProps}
    >
      {!isEndIcon && (
        <>
          {!props.isLoading && props.icon !== undefined && getIcon(props.icon)}
          {/* {!props.isLoading ? <CircularProgress id={ACCESSIBILITY_PROPS['aria-describedby']} /> : props.children} */}
          {props.isLoading ? <div className="spinner" /> : props.children}
        </>
      )}
      {isEndIcon && (
        <>
          {props.isLoading ? <div className="spinner" /> : props.children}
          {/* {!props.isLoading ? <CircularProgress id={ACCESSIBILITY_PROPS['aria-describedby']} /> : props.children} */}
          {!props.isLoading && props.icon !== undefined && getIcon(props.icon)}
        </>
      )}
      {/* {!props.isLoading && props.icon !== undefined && getIcon(props.icon)} */}
      {/* {!props.isLoading ? <CircularProgress id={ACCESSIBILITY_PROPS['aria-describedby']} /> : props.children} */}
      {/* {props.isLoading ? <div className="spinner" /> : props.children} */}
    </ButtonContainer>
  )
}

const ButtonContainer = styled(RButton)<ButtonProps & { $size?: string; $mobileFixed: boolean }>`
  border-radius: 5px;
  box-sizing: border-box;
  height: 40px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  align-items: center;
  ${compose(space, layout, typography)}
  
  ${({ $size }) => {
    switch ($size) {
      case 'verySmall':
        return 'height: 32px;'
      case 'small':
        return 'height: 40px;'
      case 'medium':
        return 'height: 48px;'
      case 'large':
        return 'height: 54px;'
      case 'extraLarge':
        return 'height: 60px;'
      default:
        return 'height: 48px;'
    }
  }}

  ${({ variant, theme }) => {
    switch (variant) {
      case 'primary':
        return `
          background-color: ${theme.colors.main};
          color: ${theme.colors.white};
          border: 0;

          &:hover, &:active {
            background-color: ${theme.colors.darkMain};
          }
        `
      case 'outline-primary':
        return `
          background-color: ${theme.colors.white};
          color: ${theme.colors.main};
          border: 2px solid ${theme.colors.main};

          &:hover, &:active {
            color: ${theme.colors.darkMain};
            border: 2px solid ${theme.colors.darkMain};
          }
        `
      case 'caution':
        return `
          background-color: ${theme.colors.caution};
          color: ${theme.colors.white};
          border: 0;

          &:hover, &:active {
            background-color: ${theme.colors.caution};
          }
        `
      default:
        return `
          background: none;
          border: 0;
        `
    }
  }}

  ${({ background }) => background && `background: ${background}`};

  &:focus {
    outline: none;
  }

  ${({ disabled, theme }) => disabled && `background: ${theme.colors.lightGrey};`}
  ${({ $mobileFixed, theme, disabled }) =>
    $mobileFixed &&
    `
      ${theme.mediaQueries.smallOnly} {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        border-radius: 0;
        height: 60px;
        z-index: 20;
        ${disabled ? 'display: none' : ''}
      }
    `}
`

const IconContainer = styled.div<{ hasChildren?: boolean; isEndIcon?: boolean }>`
  display: flex;
  ${({ isEndIcon, hasChildren }) => {
    if (isEndIcon) return 'margin-left: 5px; margin-right: 0px;'
    if (hasChildren) return 'margin-right: 10px;'
  }}
`

export default Button
